import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Timeline,
    Tag,
    Drawer,
    Input,
    message,
    Divider,
    Empty,
    List,
    Avatar,
    Row,
    Col,
    Alert,
    Spin,
    Upload,
    Tooltip,
    Popconfirm,
    Modal,
    Progress,
    Collapse,
    Steps,
    InputNumber
} from 'antd';
import {
    SendOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    CloseOutlined,
    CommentOutlined,
    DeleteOutlined,
    UserOutlined,
    UploadOutlined,
    DownloadOutlined,
    EyeOutlined,
    PaperClipOutlined,
    PlusOutlined,
    InfoCircleOutlined,
    FolderOutlined,
    AppstoreOutlined,
    TrophyOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
    HistoryOutlined,
    CheckCircleFilled,
    RobotOutlined,
    EditOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Training, Module, TrainingStatus, NestedModule } from '../types/training';
import { isUnderReview } from '../utils/reviewStatus';
import { HEX } from '../constants/reportColors';
import {
    submitManualAnswer,
    addManualComment,
    getManualComments,
    uploadFile,
    deleteFileMetadata,
    updateAssignmentState,
    startReview,
    gradeManualModule,
    saveTrainerEval,
    updateManualModule
} from '../services/moodleService';
import { draftEvaluationWithAI, extractAssignmentQuestions, QuestionResult } from '../services/adminService';
import { useAuth } from '../hooks/useAuth';
import { stripHtml } from '../utils/moduleUtils';
import { extractTextFromFile } from '../utils/fileExtraction';
import { AiEvaluationSummary } from './AiDraftReview';

const { Panel } = Collapse;

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface TrainerLedViewProps {
    training: Training;
    modules: Module[];
    nestedModules?: NestedModule[];
    onRefresh: () => void;
    isAdmin?: boolean;
    selectedUser?: any;
    initialModuleId?: string; // auto-select this module when modules load (drill-in from consolidated cell)
    onModuleChange?: (moduleId: string) => void; // notifies parent when user switches module
}
const renderTextWithLinks = (text: string) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return (
        <>
            {parts.map((part, i) => {
                if (part.match(urlRegex)) {
                    return (
                        <a 
                            key={i} 
                            href={part} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'underline', color: '#1890ff' }}
                        >
                            {part}
                        </a>
                    );
                }
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </>
    );
};

const TrainerLedView: React.FC<TrainerLedViewProps> = ({ training, modules, nestedModules, onRefresh, isAdmin: isAdminProp, selectedUser, initialModuleId, onModuleChange }) => {
    const { user } = useAuth();
    const isAdmin = isAdminProp;

    const getPreviewUrl = (file: any): string | null => {
        const ext = (file.filename as string)?.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext))
            return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(file.bloburl)}`;
        if (['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext))
            return file.previewurl || file.bloburl;
        return null;
    };

    const [selectedModule, setSelectedModule] = useState<Module | null>(() => {
        if (initialModuleId && modules.length > 0) {
            return modules.find(m => m.moduleId === initialModuleId) || modules[0] || null;
        }
        return modules[0] || null;
    });
    const appliedInitialModuleRef = React.useRef<string | null>(initialModuleId || null);
    const commentsBottomRef = React.useRef<HTMLDivElement>(null);
    const scrollToNewComment = React.useRef(false);
    const [comments, setComments] = useState<any[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [sendingComment, setSendingComment] = useState(false);
    const [studentAnswer, setStudentAnswer] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);
    const [creditInput, setCreditInput] = useState<number | null>(null);
    const [savingGrade, setSavingGrade] = useState(false);
    
    // Progress UI
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');
    const [percent, setPercent] = useState(0);
    const [pendingTransition, setPendingTransition] = useState<{ newState: string; label: string; placeholder: string; required: boolean } | null>(null);
    const [transitionNote, setTransitionNote] = useState('');
    const [evalText, setEvalText] = useState('');
    const [savingEval, setSavingEval] = useState(false);
    // AI draft modal
    const [evalModalOpen, setEvalModalOpen] = useState(false);
    const [modalInstructions, setModalInstructions] = useState('');
    const [modalAnswerText, setModalAnswerText] = useState('');
    const [modalCriteria, setModalCriteria] = useState<{ name: string }[]>([{ name: '' }]);
    const [generatingEval, setGeneratingEval] = useState(false);
    const [aiResult, setAiResult] = useState<{
        evaluation: string;
        total_score: number;
        overallConfidence?: 'high' | 'medium' | 'low';
        question_results?: QuestionResult[];
        possibly_ai_written?: { reason: string };
    } | null>(null);
    const [modalScore, setModalScore] = useState<number | null>(null);
    const [checkingSubmissionFiles, setCheckingSubmissionFiles] = useState(false);
    const [modalGenerateBlockedReason, setModalGenerateBlockedReason] = useState<string | null>(null);
    const [checkingAssignmentQuestions, setCheckingAssignmentQuestions] = useState(false);
    const [modalAssignmentQuestionNote, setModalAssignmentQuestionNote] = useState<string | null>(null);
    const [showSubmissionSection, setShowSubmissionSection] = useState(true);
    const [showAssignmentQuestionSection, setShowAssignmentQuestionSection] = useState(true);

    const isExpired = !training.active || !!(training.endDate && dayjs().isAfter(dayjs(training.endDate), 'day'));

    useEffect(() => {
        if (modules.length === 0) return;
        // If a specific module was requested (drill-in from consolidated cell), apply it once
        if (initialModuleId && appliedInitialModuleRef.current !== initialModuleId) {
            const target = modules.find(m => m.moduleId === initialModuleId);
            setSelectedModule(target || modules[0]);
            appliedInitialModuleRef.current = initialModuleId;
            return;
        }
        if (selectedModule) {
            const updated = modules.find(m => m.moduleId === selectedModule.moduleId);
            if (updated) setSelectedModule(updated);
        } else {
            setSelectedModule(modules[0]);
        }
    }, [modules, initialModuleId]);

    useEffect(() => {
        if (selectedModule && onModuleChange) onModuleChange(selectedModule.moduleId);
    }, [selectedModule?.moduleId]);

    useEffect(() => {
        if (scrollToNewComment.current) {
            commentsBottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
            scrollToNewComment.current = false;
        }
    }, [comments]);

    const handleModuleSwitch = (newModule: Module) => {
        if (selectedModule?.moduleId === newModule.moduleId) return;

        const currentAnswer = stripHtml(selectedModule?.answer || '');
        const isDirty = studentAnswer.trim() !== currentAnswer.trim() || fileList.length > 0;

        if (!isAdmin && isDirty) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes or files selected for this assignment. Are you sure you want to switch? Your changes will be lost.',
                okText: 'Yes, Switch',
                cancelText: 'No, Stay',
                onOk: () => {
                    setFileList([]);
                    setSelectedModule(newModule);
                }
            });
        } else {
            setFileList([]);
            setSelectedModule(newModule);
        }
    };


    const fetchComments = async (submissionId: number | string) => {
        if (!submissionId || submissionId === 0 || submissionId === '0') {
            setComments([]);
            return;
        }
        setLoadingComments(true);
        try {
            const data = await getManualComments(submissionId);
            setComments(data || []);
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleSendComment = async () => {
        if (!selectedModule || !newComment.trim() || !user || sendingComment) return;

        const targetUser = isAdmin && selectedUser ? (selectedUser.email || selectedUser.username) : undefined;

        setSendingComment(true);
        try {
            const response = await addManualComment({
                submissionid: selectedModule.submissionid || 0,
                assignmentid: parseInt(selectedModule.moduleId),
                userid: user.moodleId || user.username, // Prefer Moodle ID or Email
                comment: newComment,
                targetuserid: targetUser
            });

            const newSubmissionId = response.submissionid || selectedModule.submissionid;

            setNewComment('');

            // Await so button stays disabled until the new comment is visible in the list.
            // This prevents participants from double-submitting when the refresh is slow.
            scrollToNewComment.current = true;
            await fetchComments(newSubmissionId);

            // Update submissionId after the fetch to avoid triggering the useEffect's
            // fetchComments redundantly (which would cause a second network call).
            if (newSubmissionId && (!selectedModule.submissionid || selectedModule.submissionid === 0)) {
                setSelectedModule(prev => prev ? { ...prev, submissionid: newSubmissionId } : null);
            }
        } catch (error: any) {
            console.error('❌ [TrainerLedView] Failed to send comment:', error);
            message.error(`Failed to send comment: ${error.message || 'Unknown error'}`);
        } finally {
            setSendingComment(false);
        }
    };

    const handleSubmitAnswer = async () => {
        if (!selectedModule || !user) return;

        let filesWereUploaded = false;
        if (fileList.length > 0) {
            const uploadSuccess = await performBulkUpload();
            if (!uploadSuccess) return;
            filesWereUploaded = true;
        }

        // filesWereUploaded covers the case where performBulkUpload cleared fileList
        // before we can check it; selectedModule.submissionfiles covers pre-existing files
        const hasFiles = filesWereUploaded
            || (selectedModule.submissionfiles && selectedModule.submissionfiles.length > 0);

        if (!studentAnswer.trim() && !hasFiles) {
            message.warning('Please provide a text answer or upload a file for your submission.');
            return;
        }

        try {
            setSubmitting(true);
            await submitManualAnswer({
                assignmentid: selectedModule.moduleId,
                userid: user.moodleId || user.username,
                answer: studentAnswer
            });

            // Auto-advance workflow state on participant submit
            if (selectedModule.workflowEnabled) {
                const currentState = selectedModule.currentState;
                let nextState: string | null = null;
                if (!currentState) nextState = 'proposed';
                else if (currentState === 'proposed_changes') nextState = 'updated';

                if (nextState) {
                    await updateAssignmentState({
                        assignmentid: selectedModule.moduleId,
                        userid: user.moodleId || user.username,
                        newstate: nextState,
                        changedby: String(user.email || user.username || ''),
                    });
                }
            }

            message.success('Submission updated successfully');
            onRefresh();
        } catch (error) {
            message.error('Failed to submit answer');
        } finally {
            setSubmitting(false);
        }
    };

    const performBulkUpload = async (): Promise<boolean> => {
        if (!selectedModule || !user || fileList.length === 0) return true;
        
        setUploading(true);
        setProgressModalVisible(true);
        setProgressMessage(`Uploading ${fileList.length} new file${fileList.length > 1 ? 's' : ''}...`);
        setPercent(0);
        setProcessStatus('active');

        try {
            let completed = 0;
            for (const file of fileList) {
                const fileObj = file.originFileObj || file;
                setProgressMessage(`Uploading: ${fileObj.name} (${completed + 1}/${fileList.length})`);
                
                await new Promise<void>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = async () => {
                        try {
                            const base64Content = (reader.result as string || '').split(',')[1] || '';
                            await uploadFile({
                                fileName: fileObj.name,
                                fileContent: base64Content,
                                assignmentid: selectedModule.moduleId,
                                userid: user.moodleId || user.username,
                                filearea: isAdmin ? 'intro' : 'submission',
                                filetype: fileObj.type,
                                filesize: fileObj.size,
                                trainingid: training.trainingId || String(training.courseId || training.id || '')
                            });
                            completed++;
                            setPercent(Math.round((completed / fileList.length) * 100));
                            resolve();
                        } catch (err) {
                            reject(err);
                        }
                    };
                    reader.onerror = () => reject(new Error('Failed to read file'));
                    reader.readAsDataURL(fileObj);
                });
            }
            
            setProcessStatus('success');
            setProgressMessage('All files uploaded successfully');
            setFileList([]);
            onRefresh();
            setTimeout(() => setProgressModalVisible(false), 1500);
            return true;
        } catch (error) {
            setProcessStatus('exception');
            setProgressMessage('One or more files failed to upload');
            return false;
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFile = async (fileId: number) => {
        try {
            await deleteFileMetadata(fileId);
            message.success('File removed successfully');
            onRefresh();
        } catch (error) {
            message.error('Failed to remove file');
        }
    };

    const NOTE_CONFIGS: Record<string, { label: string; placeholder: string; required: boolean }> = {
        proposed:          { label: 'Submit Proposal', placeholder: 'Optional: describe what you are submitting...', required: false },
        proposed_changes:  { label: 'Request Changes', placeholder: 'What needs to change? (required)', required: true },
        updated:           { label: 'Mark as Updated', placeholder: 'What did you change? (required)', required: true },
        approved:          { label: 'Approve', placeholder: 'Approval remarks (required)', required: true },
        completed:         { label: 'Mark as Completed', placeholder: 'Completion note (required)', required: true },
    };

    const requestTransition = (newState: string) => {
        const cfg = NOTE_CONFIGS[newState] || { label: newState, placeholder: 'Optional note...', required: false };
        setTransitionNote('');
        setPendingTransition({ newState, ...cfg });
    };

    // Trainer starts reviewing this participant's submission: they then see
    // "Under review" (specs/2026-10-27-trainer-led-under-review.md). No email.
    const handleStartReview = async () => {
        if (!selectedModule || !user || !selectedUser) return;
        try {
            await startReview({
                assignmentid: selectedModule.moduleId,
                userid: selectedUser.email || selectedUser.username || '',
                reviewer: String(user.email || user.username || ''),
            });
            message.success('Review started: the participant now sees "Under review"');
            onRefresh();
        } catch (e: any) {
            message.error(`Failed to start the review: ${e.message}`);
        }
    };

    const handleMarkAsProposed = async () => {
        if (!selectedModule || !user) return;
        const participantUid = isAdmin && selectedUser
            ? (selectedUser.email || selectedUser.username || '')
            : (user.moodleId || user.username || '');
        try {
            await updateAssignmentState({
                assignmentid: selectedModule.moduleId,
                userid: participantUid,
                newstate: 'proposed',
                changedby: String(user.email || user.username || ''),
            });
            message.success('Status set to "Proposed"');
            onRefresh();
        } catch (e: any) {
            message.error(`Failed to update status: ${e.message}`);
        }
    };

    const confirmTransition = async () => {
        if (!selectedModule || !user || !pendingTransition) return;
        if (pendingTransition.required && !transitionNote.trim()) {
            message.warning('Please add a note before proceeding.');
            return;
        }
        const participantUid = isAdmin && selectedUser
            ? (selectedUser.email || selectedUser.username || '')
            : (user.moodleId || user.username || '');
        const actingUid = user.email || user.username || '';
        try {
            await updateAssignmentState({
                assignmentid: selectedModule.moduleId,
                userid: participantUid,
                newstate: pendingTransition.newState,
                changedby: String(actingUid),
                note: transitionNote.trim() || undefined,
            });
            message.success(`Status updated to "${STATE_CONFIG[pendingTransition.newState]?.label ?? pendingTransition.newState}"`);
            setPendingTransition(null);
            setTransitionNote('');
            onRefresh();
        } catch (e: any) {
            message.error(`Failed to update status: ${e.message}`);
        }
    };

    useEffect(() => {
        if (selectedModule) {
            setStudentAnswer(stripHtml(selectedModule.answer || ''));
            fetchComments(selectedModule.submissionid || 0);
            setCreditInput(selectedModule.achievedCredits != null && selectedModule.achievedCredits >= 0
                ? selectedModule.achievedCredits
                : null);
            setEvalText(selectedModule.trainerEval || '');
        }
    }, [selectedModule]);

    const handleSaveGrade = async () => {
        if (!selectedModule || !selectedUser || creditInput == null) return;
        const participantId = selectedUser.email || selectedUser.username || '';
        try {
            setSavingGrade(true);
            await gradeManualModule(selectedModule.moduleId, participantId, creditInput);
            message.success('Credits saved');
            onRefresh();
        } catch {
            message.error('Failed to save credits');
        } finally {
            setSavingGrade(false);
        }
    };

    const handleSaveEval = async () => {
        if (!selectedModule || !selectedUser) return;
        const participantId = selectedUser.userid || selectedUser.moodleId || selectedUser.email || selectedUser.username || '';
        try {
            setSavingEval(true);
            const saves: Promise<any>[] = [saveTrainerEval(selectedModule.moduleId, participantId, evalText)];
            if (creditInput != null) {
                saves.push(gradeManualModule(selectedModule.moduleId, participantId, creditInput));
            }
            const trimmedCriteria = modalCriteria
                .filter(c => c.name.trim())
                .map(c => ({ name: c.name.trim() }));
            const existingCriteria = selectedModule.evalCriteria || [];
            const criteriaChanged = JSON.stringify(trimmedCriteria) !== JSON.stringify(existingCriteria);

            const hasReferenceDocument = !!(selectedModule.introfiles && selectedModule.introfiles.length > 0);
            const trimmedInstructions = modalInstructions.trim();
            const existingExtractedQuestions = (selectedModule.extractedQuestions || '').trim();
            const questionsChanged = hasReferenceDocument && trimmedInstructions !== existingExtractedQuestions;

            if (criteriaChanged || questionsChanged) {
                saves.push(updateManualModule({
                    assignmentid: selectedModule.moduleId,
                    ...(criteriaChanged ? { evalcriteria: JSON.stringify(trimmedCriteria) } : {}),
                    ...(questionsChanged ? { extractedquestions: trimmedInstructions } : {}),
                }));
            }
            await Promise.all(saves);
            message.success('Evaluation saved');
            onRefresh();
        } catch {
            message.error('Failed to save evaluation');
        } finally {
            setSavingEval(false);
        }
    };

    const handleCancelEvalModal = () => {
        setEvalModalOpen(false);
        setAiResult(null);
        setModalCriteria(
            selectedModule?.evalCriteria && selectedModule.evalCriteria.length > 0
                ? selectedModule.evalCriteria
                : [{ name: '' }]
        );
        const baseDescription = selectedModule?.description ? selectedModule.description.replace(/<[^>]*>/g, '') : '';
        setModalInstructions(selectedModule?.extractedQuestions || baseDescription);
    };

    const handleOpenEvalModal = () => {
        const submissionFiles = selectedModule?.submissionfiles || [];
        const hasSubmission = !!studentAnswer.trim()
            || submissionFiles.length > 0
            || !!(selectedModule?.submissionid && selectedModule.submissionid > 0);
        if (!selectedModule || !hasSubmission) {
            message.warning('No submission to evaluate');
            return;
        }
        const baseDescription = selectedModule.description ? selectedModule.description.replace(/<[^>]*>/g, '') : '';
        const introfiles = selectedModule.introfiles || [];

        setModalInstructions(selectedModule.extractedQuestions || baseDescription);
        setModalAnswerText(studentAnswer);
        setModalCriteria(
            selectedModule.evalCriteria && selectedModule.evalCriteria.length > 0
                ? selectedModule.evalCriteria
                : [{ name: '' }]
        );
        setAiResult(null);
        setModalScore(creditInput);
        setModalGenerateBlockedReason(null);
        setModalAssignmentQuestionNote(null);
        setShowSubmissionSection(true);
        setShowAssignmentQuestionSection(true);
        setEvalModalOpen(true);

        if (submissionFiles.length > 0) {
            (async () => {
                setCheckingSubmissionFiles(true);
                try {
                    const results = await Promise.all(submissionFiles.map(f => extractTextFromFile(f)));
                    const firstFailure = results.find(r => !r.ok);
                    if (firstFailure) {
                        setModalGenerateBlockedReason(
                            firstFailure.reason === 'scanned-or-empty'
                                ? "AI evaluation isn't available for this submission - one of the files appears to be a scanned/image-only document. Please evaluate manually."
                                : "AI evaluation isn't available for this submission - one of the attached files isn't a supported format. Please evaluate manually."
                        );
                    } else {
                        const combined = [studentAnswer, ...results.map(r => r.text)]
                            .filter(t => t.trim())
                            .join('\n\n');
                        setModalAnswerText(combined);
                    }
                } finally {
                    setCheckingSubmissionFiles(false);
                }
            })();
        }

        if (!selectedModule.extractedQuestions && introfiles.length > 0) {
            (async () => {
                setCheckingAssignmentQuestions(true);
                try {
                    const refResults = await Promise.all(introfiles.map(f => extractTextFromFile(f)));
                    const successfulRefs = refResults.filter(r => r.ok && r.text.trim());
                    if (successfulRefs.length === 0) {
                        setModalAssignmentQuestionNote("Couldn't read the attached reference document(s) - add the questions manually if needed.");
                        return;
                    }
                    const combinedRefText = successfulRefs.map(r => r.text).join('\n\n');
                    const aiResult = await extractAssignmentQuestions({
                        assignmentTitle: selectedModule.name,
                        referenceText: combinedRefText,
                    });
                    if (!aiResult) {
                        setModalAssignmentQuestionNote("Couldn't read the attached reference document(s) - add the questions manually if needed.");
                        return;
                    }
                    if (aiResult.found && aiResult.questions.trim()) {
                        setModalInstructions(prev =>
                            prev === baseDescription
                                ? (baseDescription ? `${baseDescription}\n\n${aiResult.questions}` : aiResult.questions)
                                : prev
                        );
                    }
                } finally {
                    setCheckingAssignmentQuestions(false);
                }
            })();
        }
    };

    const handleGenerateEval = async () => {
        if (!selectedModule) return;
        const filledCriteria = modalCriteria.filter(c => c.name.trim()).map(c => ({ name: c.name.trim() }));
        const totalMax = selectedModule.maxCredits ?? 100;
        try {
            setGeneratingEval(true);
            const result = await draftEvaluationWithAI({
                assignmentTitle: selectedModule.name,
                instructions: modalInstructions,
                answerText: modalAnswerText,
                maxCredits: totalMax,
                ...(filledCriteria.length > 0 ? { criteria: filledCriteria } : {}),
            });
            if (result) {
                setAiResult(result);
                setModalScore(result.total_score);
            } else {
                message.error('AI generation failed — check the Azure Function logs');
            }
        } finally {
            setGeneratingEval(false);
        }
    };

    const handleApplyAiResult = () => {
        if (!aiResult) return;
        setEvalText(aiResult.evaluation);
        if (modalScore != null) setCreditInput(modalScore);
        setEvalModalOpen(false);
        setAiResult(null);
    };

    const getStatusIcon = (status?: string) => {
        if (status === TrainingStatus.PASSED || status === 'Successfully Completed') 
            return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    };

    return (
        <div style={{ position: 'relative' }}>
            <Row gutter={16}>
                {/* Left Column: Hierarchy Panel */}
                <Col span={4}>
                    <HierarchyPanel
                        training={training}
                        modules={modules}
                        nestedModules={nestedModules}
                        selectedModule={selectedModule}
                        onSelect={handleModuleSwitch}
                        getStatusIcon={getStatusIcon}
                    />
                </Col>

                {/* Middle Column: Assignment Content */}
                <Col span={(training.discussionEnabled ?? true) ? 16 : 20}>
                    {selectedModule ? (
                        <Card 
                            style={{ borderRadius: '12px', height: '680px', overflowY: 'auto' }}
                            bodyStyle={{ padding: '20px' }}
                        >
                            {(() => {
                                const max = selectedModule.maxCredits ?? 100;
                                const achieved = selectedModule.achievedCredits;
                                const pct = achieved != null ? Math.min(100, Math.round((achieved / max) * 100)) : null;
                                const color = pct == null ? '#8c8c8c' : pct >= 70 ? '#52c41a' : pct >= 40 ? '#fa8c16' : '#ff4d4f';
                                const bg = pct == null ? '#fafafa' : pct >= 70 ? '#f6ffed' : pct >= 40 ? '#fff7e6' : '#fff1f0';
                                const border = pct == null ? '#d9d9d9' : pct >= 70 ? '#b7eb8f' : pct >= 40 ? '#ffd591' : '#ffa39e';
                                return (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                            <Title level={4} style={{ margin: 0 }}>{selectedModule.name}</Title>
                                            <Space size={6}>
                                                {max > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, border: `1px solid ${border}`, borderRadius: 12, padding: '2px 8px' }}>
                                                    <TrophyOutlined style={{ fontSize: 11, color }} />
                                                    <Text strong style={{ fontSize: 12, color }}>{achieved != null ? achieved : '—'}</Text>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>/ {max} pts</Text>
                                                </span>}
                                                {selectedModule.status && (
                                                    <Tag color={selectedModule.status === 'Passed' ? 'success' : 'processing'} style={{ margin: 0 }}>
                                                        {selectedModule.status.toUpperCase()}
                                                    </Tag>
                                                )}
                                                {isUnderReview(selectedModule) && (
                                                    <Tooltip title={selectedModule.reviewBy ? `${selectedModule.reviewBy} is reviewing your submission` : 'A trainer is reviewing your submission'}>
                                                        <Tag icon={<EyeOutlined />} style={{ margin: 0, fontWeight: 600, background: HEX.underReviewBg, color: HEX.underReviewText, borderColor: HEX.underReviewText }}>
                                                            Under review
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </Space>
                                        </div>
                                                        <Progress percent={pct ?? 0} strokeColor={color} trailColor="#f0f0f0" showInfo={false} size="small" style={{ marginBottom: 10 }} />
                                        {(() => {
                                            const proposalTs = selectedModule.deadline ? parseInt(selectedModule.deadline) : null;
                                            const implTs = selectedModule.stateDeadlines?.completed ?? null;
                                            const finalTs = implTs || proposalTs;
                                            if (!selectedModule.workflowEnabled) {
                                                if (!finalTs) return null;
                                                return (
                                                    <Space size={4} style={{ marginBottom: 12 }}>
                                                        <ClockCircleOutlined style={{ fontSize: 12, color: '#fa8c16' }} />
                                                        <Text type="secondary" style={{ fontSize: 12 }}>Final deadline:</Text>
                                                        <Text style={{ fontSize: 12, color: '#fa8c16' }} strong>{dayjs.unix(finalTs).format('DD MMM YYYY')}</Text>
                                                    </Space>
                                                );
                                            }
                                            if (!proposalTs && !implTs) return null;
                                            return (
                                                <Space size={12} style={{ marginBottom: 12 }}>
                                                    {proposalTs && (
                                                        <Space size={4}>
                                                            <ClockCircleOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
                                                            <Text type="secondary" style={{ fontSize: 12 }}>Proposal deadline:</Text>
                                                            <Text style={{ fontSize: 12 }} strong>{dayjs.unix(proposalTs).format('DD MMM YYYY')}</Text>
                                                        </Space>
                                                    )}
                                                    {implTs && (
                                                        <Space size={4}>
                                                            <ClockCircleOutlined style={{ fontSize: 12, color: '#fa8c16' }} />
                                                            <Text type="secondary" style={{ fontSize: 12 }}>Implementation deadline:</Text>
                                                            <Text style={{ fontSize: 12, color: '#fa8c16' }} strong>{dayjs.unix(implTs).format('DD MMM YYYY')}</Text>
                                                        </Space>
                                                    )}
                                                </Space>
                                            );
                                        })()}
                                    </>
                                );
                            })()}


                            <div style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                                <Text strong type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                    <FileTextOutlined style={{ marginRight: '8px' }} />
                                    INSTRUCTIONS
                                </Text>
                                <Paragraph style={{ whiteSpace: 'pre-wrap', color: '#595959', margin: 0 }}>
                                    {renderTextWithLinks(stripHtml(selectedModule.description || 'No specific instructions provided for this module.'))}
                                </Paragraph>
                                
                                {selectedModule.introfiles && selectedModule.introfiles.length > 0 && (
                                    <div style={{ marginTop: '16px' }}>
                                        <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            REFERENCE MATERIALS
                                        </Text>
                                        <Space wrap>
                                            {selectedModule.introfiles.map((file, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d9d9d9' }}>
                                                    <Text style={{ fontSize: '12px', marginRight: '8px' }}>{file.filename}</Text>
                                                    <Space size={0}>
                                                        {getPreviewUrl(file) && (
                                                            <Button
                                                                type="link"
                                                                size="small"
                                                                icon={<EyeOutlined />}
                                                                href={getPreviewUrl(file)!}
                                                                target="_blank"
                                                            />
                                                        )}
                                                        <Button
                                                            type="link"
                                                            size="small"
                                                            icon={<DownloadOutlined />}
                                                            href={file.bloburl}
                                                            target="_blank"
                                                        />
                                                        {isAdmin && !selectedUser && file.userid === user?.moodleId && (
                                                            <Popconfirm
                                                                title="Delete this reference file?"
                                                                description="This will permanently remove the material for all participants."
                                                                onConfirm={() => handleDeleteFile(file.id)}
                                                                okText="Delete"
                                                                okType="danger"
                                                                cancelText="Cancel"
                                                            >
                                                                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                                            </Popconfirm>
                                                        )}
                                                    </Space>
                                                </div>
                                            ))}
                                            {isAdmin && !selectedUser && (
                                                <Upload 
                                                    fileList={[]}
                                                    beforeUpload={(file) => {
                                                        setFileList([file]);
                                                        // Use setTimeout to allow state update before triggering
                                                        setTimeout(() => performBulkUpload(), 100);
                                                        return false;
                                                     }}
                                                     showUploadList={false}
                                                >
                                                    <Button icon={<PlusOutlined />} size="small" type="dashed">Add Reference</Button>
                                                </Upload>
                                            )}
                                        </Space>
                                    </div>
                                )}
                            </div>

                            <Divider orientation="left" style={{ margin: '0 0 16px 0' }}>
                                <Text strong>
                                    {isAdmin ? "Student's Submission" : "Your Submission"}
                                </Text>
                            </Divider>
                            
                            <div style={{
                                marginBottom: '12px',
                                padding: '10px 14px',
                                backgroundColor: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: '8px',
                                display: 'flex',
                                gap: '10px',
                                alignItems: 'flex-start'
                            }}>
                                <span style={{ fontSize: 16, marginTop: 1 }}>💡</span>
                                <Text style={{ fontSize: 13, color: '#614700' }}>
                                    Tip: Share an editable document link with comment access instead of uploading files. Trainers can provide feedback directly, saving time and enabling instant updates.
                                </Text>
                            </div>

                            {/* There is no draft saving: the button submits straight away */}
                            {!isAdmin && (
                                <div style={{ marginBottom: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                        {selectedModule.workflowEnabled
                                            ? '* "Save & Submit" sends your work to your trainer for review. You can update and resubmit until your trainer approves it or asks for changes.'
                                            : '* "Submit" sends your work to your trainer for review. You can update and resubmit until it\'s graded.'}
                                    </Text>
                                </div>
                            )}
                            
                            {isAdmin ? (
                                <div style={{
                                    minHeight: 200,
                                    marginBottom: '16px',
                                    padding: '8px 12px',
                                    borderRadius: '8px',
                                    backgroundColor: '#fafafa',
                                    border: '1px solid #d9d9d9',
                                    color: '#262626',
                                    fontSize: 14,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {studentAnswer
                                        ? renderTextWithLinks(studentAnswer)
                                        : (selectedModule?.submissionfiles && selectedModule.submissionfiles.length > 0)
                                            ? <span style={{ color: '#bfbfbf' }}>No text — see attached file(s) below.</span>
                                            : <span style={{ color: '#bfbfbf' }}>Student has not submitted anything yet.</span>
                                    }
                                </div>
                            ) : (
                                <TextArea
                                    rows={10}
                                    value={studentAnswer}
                                    onChange={(e) => setStudentAnswer(e.target.value)}
                                    placeholder="Type your answer or submission content here..."
                                    style={{ marginBottom: '16px', borderRadius: '8px' }}
                                    disabled={isExpired}
                                />
                            )}

                            {isAdmin && selectedUser && !selectedModule.workflowEnabled && String(selectedModule.status) === 'Submitted' && selectedModule.achievedCredits == null && (
                                <div style={{ marginBottom: 12 }}>
                                    {isUnderReview(selectedModule)
                                        ? <ReviewingNote module={selectedModule} />
                                        : <Button size="small" icon={<EyeOutlined />} onClick={handleStartReview}>Start review</Button>}
                                </div>
                            )}

                            {isAdmin && selectedUser && (
                                <div style={{ marginBottom: 16 }}>
                                    {/* Award Credits row */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, padding: '10px 14px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                                        <TrophyOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                                        <Text style={{ fontSize: 13 }}>Award Credits:</Text>
                                        <InputNumber
                                            min={0}
                                            max={selectedModule.maxCredits ?? 100}
                                            value={creditInput}
                                            onChange={(val) => setCreditInput(val)}
                                            style={{ width: 80 }}
                                            placeholder="0"
                                        />
                                        <Text type="secondary" style={{ fontSize: 12 }}>/ {selectedModule.maxCredits ?? 100}</Text>
                                        {selectedModule.achievedCredits != null && selectedModule.achievedCredits >= 0 && (
                                            <Tag color="green" style={{ marginLeft: 4 }}>
                                                Current: {selectedModule.achievedCredits}
                                            </Tag>
                                        )}
                                    </div>
                                    {/* Trainer Evaluation */}
                                    <div style={{ padding: '12px 14px', background: '#f9f9f9', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <Space size={6}>
                                                <EditOutlined style={{ color: '#1890ff', fontSize: 13 }} />
                                                <Text strong style={{ fontSize: 13 }}>Trainer Evaluation</Text>
                                                <Text type="secondary" style={{ fontSize: 11 }}>(visible to participant)</Text>
                                            </Space>
                                            <Button
                                                size="small"
                                                icon={<RobotOutlined />}
                                                onClick={handleOpenEvalModal}
                                                disabled={isExpired || (!studentAnswer.trim() && !(selectedModule.submissionfiles && selectedModule.submissionfiles.length > 0))}
                                                style={{ borderRadius: 6, fontSize: 12 }}
                                            >
                                                Draft with AI
                                            </Button>
                                        </div>
                                        <Input.TextArea
                                            rows={4}
                                            value={evalText}
                                            onChange={e => setEvalText(e.target.value)}
                                            placeholder="Write your evaluation here, or click 'Draft with AI' to generate a suggestion..."
                                            style={{ borderRadius: 6, fontSize: 13, marginBottom: 8 }}
                                            disabled={isExpired}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                type="primary"
                                                loading={savingEval}
                                                disabled={isExpired || !evalText.trim()}
                                                onClick={handleSaveEval}
                                                style={{ borderRadius: 6 }}
                                            >
                                                Save Evaluation
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!isAdmin && (
                                isExpired ? (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message="Submissions Closed"
                                        description={`The program deadline was ${dayjs(training.endDate).format('DD MMM YYYY')}. New submissions are no longer accepted.`}
                                        style={{ borderRadius: 8 }}
                                    />
                                ) : (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <Upload
                                                    fileList={fileList}
                                                    onChange={({ fileList }) => setFileList(fileList)}
                                                    beforeUpload={() => false}
                                                    multiple
                                                >
                                                    <Button icon={<PlusOutlined />}>Select Files</Button>
                                                </Upload>
                                                {fileList.length > 0 && (
                                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                                        <InfoCircleOutlined style={{ color: '#1890ff', marginRight: '4px' }} />
                                                        {fileList.length} file{fileList.length > 1 ? 's' : ''} will be uploaded when you submit
                                                    </Text>
                                                )}
                                            </div>
                                        </Space>
                                        <Space size={8}>
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                loading={submitting || uploading}
                                                onClick={handleSubmitAnswer}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                {selectedModule.workflowEnabled ? 'Save & Submit' : 'Submit'}
                                            </Button>
                                            {selectedModule.workflowEnabled && selectedModule.currentState === 'approved' && (
                                                <Button
                                                    type="primary"
                                                    onClick={() => requestTransition('completed')}
                                                    style={{ borderRadius: '8px', backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
                                                >
                                                    Mark as Completed
                                                </Button>
                                            )}
                                        </Space>
                                    </div>
                                )
                            )}

                            {/* Trainer Evaluation — visible to participant (read-only) */}
                            {!isAdmin && selectedModule.trainerEval && (
                                <div style={{ marginTop: 20, padding: '12px 14px', background: '#f0f5ff', borderRadius: 8, border: '1px solid #adc6ff' }}>
                                    <Space size={6} style={{ marginBottom: 8 }}>
                                        <EditOutlined style={{ color: '#1890ff', fontSize: 13 }} />
                                        <Text strong style={{ fontSize: 13, color: '#1890ff' }}>Trainer Evaluation</Text>
                                    </Space>
                                    <Paragraph style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap', color: '#262626' }}>
                                        {selectedModule.trainerEval}
                                    </Paragraph>
                                </div>
                            )}

                            {/* Display Student's Uploaded Files */}
                            {(selectedModule.submissionfiles && selectedModule.submissionfiles.length > 0) && (
                                <div style={{ marginTop: '24px' }}>
                                    <Divider orientation="left" plain>
                                        <Space>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>ATTACHMENTS</Text>
                                            {!isAdmin && (
                                                <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                                                    <InfoCircleOutlined style={{ color: '#fa541c', marginRight: '4px' }} />
                                                    Note: Deletion is permanent and immediate (does not require 'Update Submission').
                                                </Text>
                                            )}
                                        </Space>
                                    </Divider>
                                    <List
                                        size="small"
                                        dataSource={selectedModule.submissionfiles}
                                        renderItem={(file: any) => (
                                            <List.Item
                                                extra={
                                                    <Space>
                                                        {getPreviewUrl(file) && (
                                                            <Button
                                                                type="link"
                                                                icon={<EyeOutlined />}
                                                                href={getPreviewUrl(file)!}
                                                                target="_blank"
                                                            >
                                                                Preview
                                                            </Button>
                                                        )}
                                                        <Button
                                                            type="link"
                                                            icon={<DownloadOutlined />}
                                                            href={file.bloburl}
                                                            target="_blank"
                                                        >
                                                            Download
                                                        </Button>
                                                        {!isAdmin && !isExpired && file.userid === user?.moodleId && (
                                                            <Popconfirm
                                                                title="Remove this attachment?"
                                                                description="This will permanently delete the file from your submission."
                                                                onConfirm={() => handleDeleteFile(file.id)}
                                                                okText="Delete"
                                                                okType="danger"
                                                                cancelText="Cancel"
                                                            >
                                                                <Button type="link" danger icon={<DeleteOutlined />} />
                                                            </Popconfirm>
                                                        )}
                                                    </Space>
                                                }
                                            >
                                                <List.Item.Meta
                                                    avatar={<PaperClipOutlined />}
                                                    title={<Text style={{ fontSize: '13px' }}>{file.filename}</Text>}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            )}
                        </Card>
                    ) : (
                        <Card style={{ borderRadius: '12px', height: '680px', textAlign: 'center', padding: '100px' }}>
                            <Empty description="Select a module from the timeline to view details." />
                        </Card>
                    )}
                </Col>

                {/* Right Column: Workflow + Discussion */}
                {(training.discussionEnabled ?? true) && <Col span={4}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '680px' }}>
                        {selectedModule?.workflowEnabled && (
                            <Card
                                style={{ borderRadius: '12px', flexShrink: 0 }}
                                bodyStyle={{ padding: '12px 16px' }}
                            >
                                <AssignmentStatePanel
                                    module={selectedModule}
                                    isAdmin={!!isAdmin}
                                    actingUser={isAdmin && selectedUser ? (selectedUser.email || selectedUser.username) : (user?.moodleId || user?.username || '')}
                                    participantId={isAdmin && selectedUser ? (selectedUser.email || selectedUser.username) : (user?.moodleId || user?.username || '')}
                                    onStateChanged={onRefresh}
                                    onRequestTransition={requestTransition}
                                    onMarkAsProposed={handleMarkAsProposed}
                                    onStartReview={handleStartReview}
                                />
                            </Card>
                        )}
                    {(training.discussionEnabled ?? true) && <Card
                        style={{ borderRadius: '12px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        title={
                            <Space>
                                <CommentOutlined style={{ color: '#fa541c' }} />
                                <span>Discussion Panel</span>
                            </Space>
                        }
                        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden' }}
                    >
                        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', borderBottom: '1px solid #f0f0f0' }}>
                            {loadingComments ? (
                                <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div>
                            ) : comments.length === 0 ? (
                                <Empty description="No discussion yet." />
                            ) : (
                                <List
                                    dataSource={[...comments].reverse()}
                                    itemLayout="horizontal"
                                    renderItem={(item: any) => (
                                        <List.Item style={{ padding: '12px 0' }}>
                                            <List.Item.Meta
                                                avatar={<Avatar size="small" src={`https://ui-avatars.com/api/?name=${item.username || 'User'}&background=random`} />}
                                                title={
                                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                        <Text strong>{item.username || 'User'}</Text>
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                                            {dayjs.unix(item.timecreated).format('MMM D, h:mm A')}
                                                        </Text>
                                                    </div>
                                                }
                                                description={
                                                    <Paragraph style={{ margin: 0, color: '#262626', fontSize: '13px' }}>
                                                        {renderTextWithLinks(stripHtml(item.content))}
                                                    </Paragraph>
                                                }
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                            <div ref={commentsBottomRef} />
                        </div>
                        
                        <div style={{ padding: '16px', borderTop: '1px solid #f0f0f0', background: '#fafafa' }}>
                            {isExpired ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="Discussion Closed"
                                    description="This program has ended. New messages are no longer accepted."
                                    style={{ borderRadius: 8 }}
                                />
                            ) : (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <TextArea
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder={isAdmin ? "Reply to student..." : "Ask a question..."}
                                        autoSize={{ minRows: 2, maxRows: 4 }}
                                        style={{ borderRadius: '8px' }}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        onClick={handleSendComment}
                                        loading={sendingComment}
                                        disabled={sendingComment || !newComment.trim()}
                                        block
                                        style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', borderRadius: '8px' }}
                                    >
                                        Send Message
                                    </Button>
                                </Space>
                            )}
                        </div>
                    </Card>}
                    </div>
                </Col>}
            </Row>

            <Modal
                title={pendingTransition?.label || 'Confirm Transition'}
                open={!!pendingTransition}
                onOk={confirmTransition}
                onCancel={() => { setPendingTransition(null); setTransitionNote(''); }}
                okText="Confirm"
                cancelText="Cancel"
                centered
                destroyOnClose
            >
                <TextArea
                    value={transitionNote}
                    onChange={e => setTransitionNote(e.target.value)}
                    placeholder={pendingTransition?.placeholder}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    style={{ borderRadius: 8, marginTop: 8 }}
                />
                {pendingTransition?.required && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#ff4d4f' }}>* A note is required for this action.</div>
                )}
            </Modal>

            <Modal
                title="Uploading Files"
                open={progressModalVisible}
                footer={null}
                closable={processStatus !== 'active'}
                onCancel={() => setProgressModalVisible(false)}
                centered
                destroyOnClose
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Progress
                        type="circle"
                        percent={percent}
                        status={processStatus === 'active' ? 'active' : (processStatus === 'success' ? 'success' : 'exception')}
                    />
                    <div style={{ marginTop: '20px' }}>
                        <Text strong>{progressMessage}</Text>
                    </div>
                </div>
            </Modal>

            {/* AI Evaluation Draft Modal */}
            <Modal
                title={<Space><RobotOutlined style={{ color: '#1890ff' }} /><span>Draft Evaluation with AI</span></Space>}
                open={evalModalOpen}
                onCancel={handleCancelEvalModal}
                width={1200}
                style={{ maxWidth: '95vw' }}
                centered
                destroyOnClose
                footer={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {(() => {
                            const displayMax = selectedModule?.maxCredits ?? 100;
                            return (
                                <Space size={6} align="center">
                                    <TrophyOutlined style={{ color: '#fa8c16' }} />
                                    <Text style={{ fontSize: 13 }}>Credits:</Text>
                                    <InputNumber
                                        min={0}
                                        max={displayMax}
                                        value={modalScore}
                                        onChange={val => setModalScore(typeof val === 'number' ? val : null)}
                                        style={{ width: 72 }}
                                        placeholder="—"
                                        size="small"
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>/ {displayMax}</Text>
                                </Space>
                            );
                        })()}
                        <Space>
                            <Button onClick={handleCancelEvalModal}>Cancel</Button>
                            {!aiResult ? (
                                <Button
                                    type="primary"
                                    icon={<RobotOutlined />}
                                    loading={generatingEval || checkingSubmissionFiles}
                                    disabled={!!modalGenerateBlockedReason}
                                    onClick={handleGenerateEval}
                                >
                                    Generate
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        icon={<RobotOutlined />}
                                        loading={generatingEval || checkingSubmissionFiles}
                                        disabled={!!modalGenerateBlockedReason}
                                        onClick={handleGenerateEval}
                                    >
                                        Regenerate
                                    </Button>
                                    <Button type="primary" icon={<CheckCircleOutlined />} onClick={handleApplyAiResult}>
                                        Apply
                                    </Button>
                                </>
                            )}
                        </Space>
                    </div>
                }
            >
                <Row gutter={24}>
                <Col span={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Student Submission + submission date */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <Text strong style={{ fontSize: 13 }}>Student Submission</Text>
                            <Space size={6}>
                                {(() => {
                                    const effectiveTs = (selectedModule?.stateDeadlines as any)?.completed
                                        ?? (selectedModule?.deadline ? parseInt(selectedModule.deadline) : 0);
                                    if (!effectiveTs) return null;
                                    const isLate = !!(selectedModule?.timeSubmitted && selectedModule.timeSubmitted > effectiveTs);
                                    return (
                                        <Tag color={isLate ? 'red' : 'orange'} style={{ fontSize: 11 }}>
                                            Deadline: {dayjs.unix(effectiveTs).format('DD MMM YYYY')}
                                        </Tag>
                                    );
                                })()}
                                {selectedModule?.timeSubmitted ? (
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                        Submitted: {dayjs.unix(selectedModule.timeSubmitted).format('DD MMM YYYY, hh:mm A')}
                                    </Tag>
                                ) : (
                                    <Tag color="default" style={{ fontSize: 11 }}>Not yet submitted</Tag>
                                )}
                                <Button
                                    type="link"
                                    size="small"
                                    style={{ padding: 0, height: 'auto', fontSize: 11 }}
                                    onClick={() => setShowSubmissionSection(v => !v)}
                                >
                                    {showSubmissionSection ? 'Hide' : 'Show'}
                                </Button>
                            </Space>
                        </div>
                        {showSubmissionSection && (
                            <>
                                <Input.TextArea
                                    rows={5}
                                    value={modalAnswerText}
                                    onChange={e => setModalAnswerText(e.target.value)}
                                    placeholder="Paste or type the student's submission content here..."
                                    style={{ borderRadius: 6, fontSize: 13 }}
                                />
                                {modalGenerateBlockedReason && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message={modalGenerateBlockedReason}
                                        style={{ marginTop: 8, fontSize: 12 }}
                                    />
                                )}
                            </>
                        )}
                    </div>

                    {/* Assignment Question */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Assignment Question</Text>
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', fontSize: 11 }}
                                onClick={() => setShowAssignmentQuestionSection(v => !v)}
                            >
                                {showAssignmentQuestionSection ? 'Hide' : 'Show'}
                            </Button>
                        </div>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                            Pre-filled from the assignment description{checkingAssignmentQuestions ? ' - checking attached reference document(s)...' : ''}. AI uses this as task context.
                        </Text>
                        {showAssignmentQuestionSection && (
                        <>
                        <Input.TextArea
                            rows={3}
                            value={modalInstructions}
                            onChange={e => setModalInstructions(e.target.value)}
                            placeholder="Describe what the student was expected to do..."
                            style={{ borderRadius: 6, fontSize: 13 }}
                        />
                        {modalAssignmentQuestionNote && (
                            <Alert
                                type="info"
                                showIcon
                                message={modalAssignmentQuestionNote}
                                style={{ marginTop: 8, fontSize: 12 }}
                            />
                        )}
                        </>
                        )}
                    </div>
                </div>
                </Col>
                <Col span={12}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* Evaluation Criteria - the trainer's own checklist. The AI takes it
                        into account when judging the questions; it is not scored. */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div>
                                <Text strong style={{ fontSize: 13 }}>Evaluation Criteria</Text>
                                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                                    What AI should check in every answer
                                </Text>
                            </div>
                            <Button
                                size="small"
                                type="dashed"
                                icon={<PlusOutlined />}
                                onClick={() => setModalCriteria(prev => [...prev, { name: '' }])}
                                style={{ borderRadius: 6 }}
                            >
                                Add
                            </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {modalCriteria.map((c, i) => (
                                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <Input
                                        value={c.name}
                                        onChange={e => setModalCriteria(prev => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                                        placeholder={i === 0 ? 'e.g. Clarity of explanation' : i === 1 ? 'e.g. Practical approach' : 'e.g. Depth of analysis'}
                                        style={{ flex: 1, borderRadius: 6, fontSize: 13 }}
                                    />
                                    <Button
                                        size="small"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        disabled={modalCriteria.length === 1}
                                        onClick={() => setModalCriteria(prev => prev.filter((_, idx) => idx !== i))}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {aiResult && (
                        <AiEvaluationSummary
                            evaluation={aiResult.evaluation}
                            onEvaluationChange={text => setAiResult(prev => prev ? { ...prev, evaluation: text } : null)}
                            overallConfidence={aiResult.overallConfidence}
                            questionResults={aiResult.question_results}
                            possiblyAiWritten={aiResult.possibly_ai_written}
                            score={modalScore ?? 0}
                            maxCredits={selectedModule?.maxCredits ?? 100}
                        />
                    )}
                </div>
                </Col>
                </Row>
            </Modal>
        </div>
    );
};

export default TrainerLedView;

// ===== "Under review" note (trainer) =====

const ReviewingNote: React.FC<{ module: Module }> = ({ module }) => (
    <Text style={{ fontSize: 12, color: HEX.underReviewText }}>
        <EyeOutlined /> Under review{module.reviewBy ? ` by ${module.reviewBy}` : ''}
    </Text>
);

// ===== Assignment State Panel =====

const STATE_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    proposed:          { label: 'Proposed',          color: '#1890ff', icon: <SendOutlined /> },
    proposed_changes:  { label: 'Changes Requested', color: '#ff4d4f', icon: <ExclamationCircleOutlined /> },
    updated:           { label: 'Updated',           color: '#722ed1', icon: <ReloadOutlined /> },
    approved:          { label: 'Approved',          color: '#52c41a', icon: <CheckCircleOutlined /> },
    completed:         { label: 'Completed',         color: '#13c2c2', icon: <CheckCircleFilled /> },
};
const STATE_ORDER = ['proposed', 'proposed_changes', 'updated', 'approved', 'completed'];

interface AssignmentStatePanelProps {
    module: Module;
    isAdmin: boolean;
    actingUser: string | number;
    participantId: string | number;
    onStateChanged: () => void;
    onRequestTransition: (newState: string) => void;
    onMarkAsProposed?: () => void;
    onStartReview?: () => void;
}

const AssignmentStatePanel: React.FC<AssignmentStatePanelProps> = ({ module, isAdmin, onStateChanged, onRequestTransition, onMarkAsProposed, onStartReview }) => {
    const [historyModalOpen, setHistoryModalOpen] = React.useState(false);
    const current = module.currentState || '';
    const deadlines = module.stateDeadlines || {};
    const history = module.stateHistory || [];

    // Determine which steps are done / in-progress / pending
    const currentIdx = STATE_ORDER.indexOf(current);
    const stepItems = STATE_ORDER.map((key, idx) => {
        const cfg = STATE_CONFIG[key];
        const dl = deadlines[key];
        const histEntry = [...history].reverse().find(h => h.state === key);
        const isDone = histEntry != null;
        const isCurrent = key === current;

        let status: 'finish' | 'process' | 'wait' | 'error' = 'wait';
        if (isDone && !isCurrent) status = 'finish';
        else if (isCurrent) status = key === 'proposed_changes' ? 'error' : 'process';

        return {
            key,
            title: <Text style={{ fontSize: 11, color: isCurrent ? cfg.color : undefined }}>{cfg.label}</Text>,
            description: (
                <div style={{ fontSize: 10, lineHeight: 1.4 }}>
                    {histEntry ? <div style={{ color: cfg.color }}>Done: {dayjs.unix(histEntry.ts).format('DD MMM HH:mm')}</div> : null}
                </div>
            ),
            status,
        };
    });

    const adminActions: React.ReactNode[] = [];
    let reviewAction: React.ReactNode = null;
    if (isAdmin) {
        const hasOrphanedSubmission = !current && (
            (module.submissionfiles && module.submissionfiles.length > 0) ||
            !!(module.submissionid && module.submissionid > 0)
        );
        if (hasOrphanedSubmission) {
            adminActions.push(
                <Button key="sp" size="small" type="primary"
                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    onClick={() => onMarkAsProposed?.()}>
                    Mark as Proposed
                </Button>
            );
        }
        if (current === 'proposed' || current === 'updated') {
            reviewAction = isUnderReview(module)
                ? <ReviewingNote module={module} />
                : <Button block size="small" icon={<EyeOutlined />} onClick={() => onStartReview?.()}>Start review</Button>;
            adminActions.push(
                <Button key="pc" size="small" danger onClick={() => onRequestTransition('proposed_changes')}>Request Changes</Button>,
                <Button key="ap" size="small" type="primary"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    onClick={() => onRequestTransition('approved')}>Approve</Button>
            );
        }
    }

    return (
        <>
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Text strong style={{ fontSize: 12 }}>Workflow Status</Text>
                    {history.length > 0 && (
                        <Button
                            size="small"
                            type="default"
                            icon={<HistoryOutlined />}
                            onClick={() => setHistoryModalOpen(true)}
                        >
                            History ({history.length})
                        </Button>
                    )}
                </div>
                <Steps
                    direction="vertical"
                    size="small"
                    current={Math.max(0, currentIdx)}
                    items={stepItems}
                    style={{ marginBottom: adminActions.length > 0 ? 4 : 0 }}
                />
                {/* Start review on its own row: the side panel is too narrow for three buttons */}
                {reviewAction && (
                    <div style={{ marginTop: 4, marginBottom: 6 }}>{reviewAction}</div>
                )}
                {adminActions.length > 0 && (
                    <Space size={6} wrap style={{ marginTop: 4 }}>{adminActions}</Space>
                )}
            </div>

            <Modal
                title="Iteration History"
                open={historyModalOpen}
                onCancel={() => setHistoryModalOpen(false)}
                footer={<Button onClick={() => setHistoryModalOpen(false)}>Close</Button>}
                width={800}
                centered
                destroyOnClose
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '70vh', overflowY: 'auto', paddingRight: 8 }}>
                    {[...history].reverse().map((h, i) => {
                        const cfg = STATE_CONFIG[h.state];
                        return (
                            <div key={i} style={{ borderLeft: `3px solid ${cfg?.color ?? '#d9d9d9'}`, paddingLeft: 12 }}>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: h.note ? 6 : 0 }}>
                                    <Tag style={{
                                        fontSize: 12, padding: '1px 8px', margin: 0,
                                        background: (cfg?.color ?? '#8c8c8c') + '18',
                                        borderColor: cfg?.color ?? '#8c8c8c',
                                        color: cfg?.color ?? '#8c8c8c',
                                    }}>
                                        {cfg?.label ?? h.state}
                                    </Tag>
                                    <Text type="secondary" style={{ fontSize: 13 }}>{dayjs.unix(h.ts).format('DD MMM YYYY, HH:mm')}</Text>
                                    {h.by && isNaN(Number(h.by)) && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>· {h.by}</Text>
                                    )}
                                </div>
                                {h.note && (
                                    <div style={{ color: '#434343', fontSize: 14, lineHeight: 1.7, background: '#f9f9f9', borderRadius: 6, padding: '8px 10px' }}>
                                        {h.note}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Modal>
        </>
    );
};

// ===== Hierarchy Panel Component =====
interface HierarchyPanelProps {
    training: Training;
    modules: Module[];
    nestedModules?: NestedModule[];
    selectedModule: Module | null;
    onSelect: (mod: Module) => void;
    getStatusIcon: (status?: string) => React.ReactNode;
}

const HierarchyPanel: React.FC<HierarchyPanelProps> = ({ training, modules, nestedModules, selectedModule, onSelect, getStatusIcon }) => {
    const [openModuleKeys, setOpenModuleKeys] = React.useState<string[]>([]);
    const [openSubModuleKeys, setOpenSubModuleKeys] = React.useState<Record<string, string[]>>({});

    // Build a lookup from moodleId (string) → Module for live submission data
    const moduleByMoodleId = React.useMemo(() => {
        const map: Record<string, Module> = {};
        modules.forEach(m => { map[m.moduleId] = m; });
        return map;
    }, [modules]);

    // Use nestedModules prop (from reconstructFromLiveModules) if it has named modules
    const hasHierarchy = nestedModules && nestedModules.length > 0 && nestedModules.some(m => m.name !== '');
    const hierarchy: NestedModule[] | null = hasHierarchy ? nestedModules! : null;

    // Initialise open keys once when hierarchy first loads (never reset on re-render)
    React.useEffect(() => {
        if (!hierarchy) return;
        setOpenModuleKeys(prev => prev.length > 0 ? prev : hierarchy.map(m => m.id));
        setOpenSubModuleKeys(prev => {
            if (Object.keys(prev).length > 0) return prev;
            const init: Record<string, string[]> = {};
            hierarchy.forEach(m => {
                const subs = m.subModules || [];
                if (subs.length > 0) init[m.id] = subs.map(s => s.id);
            });
            return init;
        });
    }, [hierarchy]);

    const isSelected = (mod: Module) => selectedModule?.moduleId === mod.moduleId;

    const assignmentItem = (mod: Module, label?: string) => (
        <div
            key={mod.moduleId}
            onClick={() => onSelect(mod)}
            style={{
                padding: '6px 8px',
                borderRadius: 6,
                backgroundColor: isSelected(mod) ? '#fff7e6' : 'transparent',
                border: isSelected(mod) ? '1px solid #ffe58f' : '1px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: 4
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{getStatusIcon(mod.status)}</span>
                <Text style={{ fontSize: 12, fontWeight: isSelected(mod) ? 600 : 400, color: isSelected(mod) ? '#fa541c' : 'inherit', flex: 1 }}>
                    {label || mod.name}
                </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: 18 }}>
                {(() => {
                    const effectiveTs = (mod.stateDeadlines as any)?.completed ?? (mod.deadline ? parseInt(mod.deadline) : null);
                    return effectiveTs ? (
                        <Text type="secondary" style={{ fontSize: 10 }}>
                            Due: {dayjs.unix(effectiveTs).format('MMM DD')}
                        </Text>
                    ) : null;
                })()}
                {(() => {
                    const max = mod.maxCredits ?? 100;
                    const achieved = mod.achievedCredits;
                    const pct = achieved != null ? (achieved / max) * 100 : null;
                    const color = pct == null ? '#bfbfbf' : pct >= 70 ? '#52c41a' : pct >= 40 ? '#fa8c16' : '#ff4d4f';
                    return (
                        <Text style={{ fontSize: 10, color, fontWeight: achieved != null ? 600 : 400 }}>
                            {achieved != null ? achieved : '—'}/{max}pts
                        </Text>
                    );
                })()}
            </div>
        </div>
    );

    // Hierarchical view using reconstructed nested modules
    if (hierarchy) {
        return (
            <Card
                style={{ borderRadius: 12, height: 680, overflowY: 'auto' }}
                title={<span style={{ fontSize: 13 }}>Program Structure</span>}
                bodyStyle={{ padding: '8px 6px' }}
            >
                <Collapse
                    activeKey={openModuleKeys}
                    onChange={keys => setOpenModuleKeys(keys as string[])}
                    bordered={false}
                    expandIconPosition="end"
                    size="small"
                    style={{ background: 'transparent' }}
                >
                    {hierarchy.map((nestedMod, modIdx) => {
                        const structure = nestedMod.structureType || 'structured';
                        const directAssignments = (structure === 'direct' || structure === 'mixed')
                            ? (nestedMod.assignments || []).filter(a => a.moodleId && moduleByMoodleId[String(a.moodleId)])
                            : [];
                        const subModules = (structure === 'structured' || structure === 'mixed')
                            ? (nestedMod.subModules || [])
                            : [];
                        const totalCount = directAssignments.length + subModules.reduce((s, sub) => s + (sub.assignments || []).length, 0);

                        return (
                            <Panel
                                key={nestedMod.id}
                                header={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <AppstoreOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                                        <Text strong style={{ fontSize: 12, color: '#1890ff' }}>
                                            M{modIdx + 1}: {nestedMod.name}
                                        </Text>
                                        <Tag style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }} color="blue">{totalCount}</Tag>
                                    </div>
                                }
                                style={{ background: '#f0f5ff', borderRadius: 6, marginBottom: 4, border: 'none' }}
                            >
                                {/* Direct assignments */}
                                {directAssignments.map(a => {
                                    const liveModule = moduleByMoodleId[String(a.moodleId)];
                                    return (
                                        <div key={a.id} style={{ paddingLeft: 4 }}>
                                            {assignmentItem(liveModule, a.title)}
                                        </div>
                                    );
                                })}

                                {/* Sub-modules — each also collapsible */}
                                {subModules.length > 0 && (
                                    <Collapse
                                        activeKey={openSubModuleKeys[nestedMod.id] || []}
                                        onChange={keys => setOpenSubModuleKeys(prev => ({ ...prev, [nestedMod.id]: keys as string[] }))}
                                        bordered={false}
                                        expandIconPosition="end"
                                        size="small"
                                        style={{ background: 'transparent', marginTop: directAssignments.length > 0 ? 4 : 0 }}
                                    >
                                        {subModules.map(sub => {
                                            const subAssignments = (sub.assignments || []).filter(a => a.moodleId && moduleByMoodleId[String(a.moodleId)]);
                                            return (
                                                <Panel
                                                    key={sub.id}
                                                    header={
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <FolderOutlined style={{ color: '#52c41a', fontSize: 11 }} />
                                                            <Text style={{ fontSize: 11, color: '#389e0d' }}>{sub.name}</Text>
                                                            <Tag style={{ marginLeft: 4, fontSize: 10, lineHeight: '16px', padding: '0 4px' }} color="green">{subAssignments.length}</Tag>
                                                        </div>
                                                    }
                                                    style={{ background: '#f6ffed', borderRadius: 4, marginBottom: 3, border: 'none' }}
                                                >
                                                    {subAssignments.map(a => {
                                                        const liveModule = moduleByMoodleId[String(a.moodleId)];
                                                        return (
                                                            <div key={a.id} style={{ paddingLeft: 8 }}>
                                                                {assignmentItem(liveModule, a.title)}
                                                            </div>
                                                        );
                                                    })}
                                                </Panel>
                                            );
                                        })}
                                    </Collapse>
                                )}
                            </Panel>
                        );
                    })}
                </Collapse>
            </Card>
        );
    }

    // Fallback: flat timeline view (for programs without assessments JSON)
    return (
        <Card
            style={{ borderRadius: 12, height: 680, overflowY: 'auto' }}
            title={<span style={{ fontSize: 13 }}>Assignments</span>}
            bodyStyle={{ padding: '8px 6px' }}
        >
            <Timeline mode="left">
                {modules.map((mod, index) => (
                    <Timeline.Item
                        key={mod.moduleId}
                        dot={getStatusIcon(mod.status)}
                        color={isSelected(mod) ? '#fa541c' : 'gray'}
                        style={{ paddingBottom: 16 }}
                    >
                        <div onClick={() => onSelect(mod)}
                            style={{
                                padding: '4px 8px', borderRadius: 6,
                                backgroundColor: isSelected(mod) ? '#fff7e6' : 'transparent',
                                cursor: 'pointer', transition: 'all 0.3s',
                                border: isSelected(mod) ? '1px solid #ffe58f' : '1px solid transparent'
                            }}
                        >
                            <Text strong style={{ display: 'block', fontSize: 12, color: isSelected(mod) ? '#fa541c' : 'inherit' }}>
                                {mod.name}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>
                                {(() => {
                                    const effectiveTs = (mod.stateDeadlines as any)?.completed ?? (mod.deadline ? parseInt(mod.deadline) : null);
                                    return effectiveTs ? `Due: ${dayjs.unix(effectiveTs).format('MMM DD')}` : `Asmt ${index + 1}`;
                                })()}
                            </Text>
                        </div>
                    </Timeline.Item>
                ))}
            </Timeline>
        </Card>
    );
};
